/** Auth session / cookie / storage-key / MFE-path SSOT. Imports only sibling pure leaves, so other `const/*.ts` leaves can consume it without the `export *` TDZ cycle a barrel import causes. */

import { TIME_UNITS } from '@federa/const/time-units';
import { USER_ROLES } from '@federa/const/auth';
import { SAME_SITE_LAX } from '@federa/const/same-site-values';
import { DEFAULT_MAX_RETRIES } from '@federa/const/timing';
import type { CallerType } from '@federa/types/adapters-caller-context';
import type { RetryConfig } from '@federa/types/api-response';

export const PHONE_VALIDATION = {
	EMPTY: 'Enter the phone number associated with your account.',
	MISSING_COUNTRY_CODE: 'Phone numbers must include the country code and start with +.',
} as const;

export const AUTH_ERRORS = {
	INVALID_CODE: 'Enter the 6-digit verification code sent to your phone.',
	PHONE_NUMBER_MISSING: 'We were unable to determine your phone number. Please restart the sign-in process.',
	VERIFICATION_FAILED: 'We could not verify that code. Please try again.',
	RESEND_FAILED: "We couldn't resend the code. Please try again later.",
} as const;

export const AUTH_INFO = {
	CODE_RESENT: 'A new verification code was sent.',
} as const;

export const OTP_CODE_LENGTH = 6;

/** ITU-T E.164: `+` prefix, first digit 1-9, then 1-14 more (2-15 digits total). */
export const E164_REGEX = /^\+[1-9]\d{1,14}$/;

/** Signed app-session surface, grouped (QUAL-CONST-GROUP-001: HTTP_STATUS pattern). */
export const APP_SESSION = {
	/** httpOnly cookie carrying the HMAC-signed app session token (`utils/security/app-session-token.ts`). The ONLY cookie a server render may derive a subject from. */
	COOKIE_NAME: 'federa_app_session',
	/** App session token lifetime. */
	TOKEN_TTL_SEC: 30 * TIME_UNITS.DAY_S,
	/** Same-origin route on `apps/federa` that mints (POST) and clears (DELETE) the signed app session cookie. */
	ROUTE_PATH: '/api/auth/session',
	/**
	 * CLIENT-READABLE companion set beside `COOKIE_NAME` by the same mint, carrying TWO facts about the signed session:
	 * when it lapses (epoch seconds) and WHOSE it is (the app user id) -- `components/lib/auth/app-session-companion.ts`
	 * owns the format. It lets a new tab skip a redundant mint while the signed cookie is still live AND still this
	 * person's. NO SERVER READS IT: every server render still verifies the signed cookie, so a forged or stale value can
	 * cost at most one extra mint, never access and never another person's session.
	 *
	 * THE WIRE VALUE STILL SAYS `_exp` AND STAYS THAT WAY. It predates the second fact, so it now names only one of the
	 * two -- but a cookie name is not a symbol: renaming it would leave the OLD name set in every browser that already
	 * holds one, with no writer left to expire it, so the cure for a stale name would be a cookie that never goes away.
	 * The rename that mattered happened on the SYMBOL (EXPIRY_COOKIE_NAME -> this), which is what a reader here sees;
	 * a bare pre-rename expiry value parses to `null` and reads as "mint", which the sibling suite pins.
	 */
	COMPANION_COOKIE_NAME: 'federa_app_session_exp',
	/** A session with less than this left is re-minted rather than trusted for the rest of its life. */
	REMINT_MARGIN_SEC: TIME_UNITS.DAY_S,
} as const;

/**
 * The attributes EVERY server-set session cookie in the fleet carries -- the signed app session (`apps/federa`) and the
 * single sign-on hub's session (`apps/ifederated`). Shared by the writer that SETS one and the writer that CLEARS it,
 * so a clear cannot miss an attribute and leave the cookie alive: httpOnly (no `document.cookie` reader), Secure,
 * SameSite=Lax, whole site.
 */
export const SERVER_SESSION_COOKIE_ATTRIBUTES = { httpOnly: true, secure: true, sameSite: SAME_SITE_LAX, path: '/' } as const;

/**
 * Bounded retry for the DELETE that ENDS a server-set session (`createServerSessionSync`'s clear).
 *
 * WHY THE CLEAR IS RETRIED AND THE OPEN IS NOT -- they fail in opposite directions. A failed OPEN leaves the person
 * signed out, which the next identity resolution retries for free. A failed clear on the HUB instance leaves the
 * session cookie in the browser, live and UNREVOKED, and the only thing then holding the fleet's other origins back is
 * each one's own sign-out marker, which lapses on a TIMER rather than on a confirmed revoke. Once it lapses the next
 * quiet ask is answered with a grant and the person is signed back in BY THE ACT OF SIGNING OUT; on a shared machine
 * that is the next person, signed in as the last one.
 *
 * ONLY A THROWN request is retried -- the wire, never a verdict. A server that ANSWERS resolves with a status, and the
 * route expires the cookie on that same response, so re-asking would re-send a decision already made. Three attempts
 * at 250 ms then 500 ms: sub-second, against the 45-second settle window (`SSO_TTL_SEC.SIGN_OUT_SETTLE`) this has to
 * stay inside.
 */
export const SERVER_SESSION_CLEAR_RETRY: RetryConfig = {
	maxAttempts: DEFAULT_MAX_RETRIES,
	baseDelayMs: 250,
	maxDelayMs: 2 * TIME_UNITS.SECOND_MS,
};

export const DEFAULT_USER_ROLE = USER_ROLES.USER;

export const DEFAULT_GUEST_ROLE = 'Guest';

export const PLACEHOLDER_USERNAME = 'User';

export const PLACEHOLDER_MFA_PHONE = 'Unknown' as const;

export const SENTINEL_SYSTEM_USER_ID = 'system';

export const GUEST_ID_PREFIX = 'guest_';

/** A SIGNED-IN user's id: present, non-empty, and not a minted guest id ({@link GUEST_ID_PREFIX}). Narrows, so a caller
 *  past the guard holds the id as a string. Homed beside the prefix it reads so the `utils/auth` viewer resolver can
 *  reach it too, and so no signed-in path spells `!userId || userId.startsWith(GUEST_ID_PREFIX)` for itself.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the one signed-in test over {@link GUEST_ID_PREFIX}. */
export function isSignedInUserId(userId: string | null | undefined): userId is string {
	return typeof userId === 'string' && userId !== '' && !userId.startsWith(GUEST_ID_PREFIX);
}

export const PLACEHOLDER_GUEST_IDENTITY = 'Guest';

export const PLACEHOLDER_ANONYMOUS_IDENTITY = 'anonymous';

export const DEFAULT_AUTH_METHOD = 'phone_otp';

/** participants.control values. ADMIN is full control (`LABEL_FULL_CONTROL` in the UI); EDITOR is the write control
 *  an invite posts for every role but a viewer (`defaultControlForRole`). */
export const PARTICIPANT_CONTROLS = {
	ADMIN: 'admin',
	EDITOR: 'editor',
} as const;

/**
 * participants.control values granting write access to a conversation.
 *
 * `signer` AND `approver` HAVE NO IN-TREE PRODUCER, and the cut was proposed and DECLINED on 2026-09-03. Measured with
 * word boundaries over 33,142 tracked text files, controls in the same invocation: `editor` 303 occurrences / 174
 * files and `viewer` 1327 / 568 both fire; a nonsense token returns 0. `signer`'s 17 hits are a different sense
 * entirely (token codec, closing-doc PDF constants, corporate-resolution signatories) and `approver`'s 109 are all
 * `TEAM_ROLES.APPROVER` in the finance domain. `control\s*[:=]\s*['"](signer|approver)['"]` matches NOWHERE, and every
 * in-tree writer -- `transaction-participant-add`, `user-wishlist-participant-add`, `wishlist-ws-handlers`,
 * `AddParticipantModal` -- produces `editor` or `viewer` only.
 *
 * WHY IT STAYS ANYWAY, and the ground is stronger than "it might be a future contract". This array is an ACL, so
 * removing a member REVOKES write access. `participants.control` is free-form TEXT with NO CHECK (see
 * `migrations/federa-db/0004_connect_voice.sql`, which names this const as the gate), and the three add-participant
 * paths pass a CALLER-SUPPLIED `control` through verbatim. So a stored row carrying `signer` is reachable from any
 * historical caller, and whether one EXISTS is a question about production DATA that no census of this repository can
 * answer. A tree-only measurement establishes no in-repo WRITER; it cannot establish no stored ROW, and the cost of
 * being wrong is a real participant silently losing write access to their own conversation.
 *
 * It is also NOT a privilege-escalation surface: a caller who can supply `signer` could equally supply `editor`. What
 * it IS: the guard reads as covering four access levels while the product produces two.
 *
 * NOT A UNILATERAL CUT EVEN IF THE DATA QUESTION IS ANSWERED. `QUAL-PARTICIPANT-CONTROL-VALUE-SSOT-001` DERIVES its
 * accept-set from this array and names both members verbatim in its `fix` prose, so a bare cut would narrow a live
 * HIGH matcher AND leave its cure text instructing a value the same rule would then convict. Both hunks land together
 * or neither does.
 */
export const WRITE_ACCESS_CONTROLS = [PARTICIPANT_CONTROLS.ADMIN, PARTICIPANT_CONTROLS.EDITOR, 'signer', 'approver'] as const;

/** `participants.role` values. The column is free-form TEXT with NO CHECK constraint, so this record -- not the
 * schema -- is the vocabulary, and every producer and comparison reads it.
 *
 * OWNER is the conversation creator: IMMUTABLE, RESERVED (both `transaction-participant-add` and
 * `transaction-participant-update` refuse it outright), and grants write access regardless of control. MEMBER and
 * VIEWER are the two the roster may ASSIGN -- `AddParticipantModal`'s `LABEL_CAN_EDIT` / `LABEL_CAN_VIEW_ONLY` pair,
 * the latter posting `PARTICIPANT_DEFAULTS.CONTROL` alongside it -- and {@link AssignableParticipantRole}
 * (`types/ui-chat-messaging.ts`) is that subset, derived from these members so the pair cannot drift.
 *
 * NOT exhaustive of what LIVE ROWS carry: `transaction-participant-add` defaults an omitted role to `co-buyer`, and
 * `PARTICIPANT_DEFAULTS.ROLE` / `ADMIN_ROLE` spell two more. Those are unadopted members rather than a second
 * vocabulary -- adding them here needs a live-row read first, because a wrong member here becomes a wrong comparison
 * everywhere at once. */
export const PARTICIPANT_ROLES = {
	OWNER: 'owner',
	MEMBER: 'member',
	VIEWER: 'viewer',
} as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the membership test over {@link PARTICIPANT_ROLES}, homed beside
 * the record it reads so the value and the one predicate that interprets it cannot drift, and so BOTH consumer layers
 * can value-import it -- `components/modals/SaveToWishlistModal.tsx` and the wishlists MFE views sit in different
 * packages with no shared non-const leaf between them, which is what left three copies of `role === 'viewer'` in the
 * tree until 2026-08-20.
 *
 * Does a participant ROLE carry the write affordances -- delete, rename, roster management?
 *
 * THE CLIENT-SIDE HALF of the write ACL, and deliberately a different projection from the server's. A flow gates on the
 * authoritative `role` / `control` pair (`hasWriteAccess`, `tools/conversations/participant-write-gate.ts`); a
 * component only ever holds the `role` the list query projects, so this is what it may ask. It is an AFFORDANCE gate:
 * it decides which controls render, never whether a write is allowed to land.
 *
 * ABSENT IS PERMISSIVE, and that is the pre-existing behaviour rather than a new choice: `WishlistPageItem.role` is
 * optional and a row that carries none (an optimistic create, a projection predating the column) rendered its controls
 * before this predicate existed. Refusing on `undefined` would hide the delete on every just-created wishlist.
 */
export function canManageAsRole(role: string | undefined): boolean {
	return role !== PARTICIPANT_ROLES.VIEWER;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the membership test over {@link WRITE_ACCESS_CONTROLS} plus the
 * OWNER short-circuit, homed beside the vocabulary it reads on `canManageAsRole`'s own grounds -- four packages
 * (tools, utils, flows, the navigator MFE) each hand-rolled this pair until 2026-08-22, and every one can
 * value-import `@federa/const`.
 *
 * Does this participant's role/control pair grant WRITE access? OWNER writes regardless of control (the creator's
 * row is immutable); otherwise the LOWERCASED control must sit in the write-granting vocabulary. The lowercasing is
 * load-bearing, not defensive: every caller reads `participants.control` in CONVERSATIONS_DB, a free-form TEXT
 * column with NO CHECK, so live rows carry whatever case their writer used and a bare membership test would refuse
 * a genuine editor.
 */
export function participantHasWriteAccess(role: string | null | undefined, control: string | null | undefined): boolean {
	if (role === PARTICIPANT_ROLES.OWNER) return true;
	return (WRITE_ACCESS_CONTROLS as readonly string[]).includes((control ?? '').toLowerCase());
}

/** New-participant defaults: least-privilege control, non-owner role. */
export const PARTICIPANT_DEFAULTS = {
	ROLE: 'participant',
	CONTROL: 'viewer',
	/** Role for system-created admin conversation participants. */
	ADMIN_ROLE: 'Admin',
	NAME: 'Participant',
} as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the control an invite posts for the role it assigns -- the
 * least-privilege default for a VIEWER, edit access for every other role. Read off {@link PARTICIPANT_DEFAULTS} and
 * {@link PARTICIPANT_CONTROLS}; the transaction participant-add flow and the wishlist invite modal each spelled it inline.
 */
export function defaultControlForRole(role: string): typeof PARTICIPANT_DEFAULTS.CONTROL | typeof PARTICIPANT_CONTROLS.EDITOR {
	return role === PARTICIPANT_ROLES.VIEWER ? PARTICIPANT_DEFAULTS.CONTROL : PARTICIPANT_CONTROLS.EDITOR;
}

export const APP_STATE_STORAGE_KEY = 'federa-app-state';

export const ADMIN_APP_STATE_STORAGE_KEY = 'admin-federa-app-state';

export const SESSION_STATE_STORAGE_KEY = 'federa_session_state';

export const GUEST_ID_STORAGE_KEY = 'federa_guest_id';

/** The frontend replays this as the WS bearer credential for guest users -- a shared contract with the ChatRoom upgrade verifier. */
export const GUEST_SESSION_TOKEN_STORAGE_KEY = 'federa_guest_session_token';

export const GUEST_SESSION_TOKEN_TTL_SEC = 30 * TIME_UNITS.DAY_S;

/**
 * The storage-gateway guest identity is SERVER-MINTED (`guest-session-issue`), so it is persisted under keys of its own
 * rather than sharing `GUEST_ID_STORAGE_KEY` / `GUEST_SESSION_TOKEN_STORAGE_KEY` with the chats guest session. Two
 * reasons, both silent-failure classes: the chats keys hold a token signed over a CONVERSATION binding this surface
 * rejects, and a shared key means whichever surface writes last evicts the other's live credential.
 */
export const EXPLORE_GUEST_ID_STORAGE_KEY = 'federa_explore_guest_id';

/** The profile id whose TOKENLESS invite attribution this browser already asked about (`useInviteClaim`). The tokenless
 *  claim exists to catch the joiner who ignored the SMS link, and it needs asking ONCE per account -- it was asked on
 *  every Contacts tab open (measured: a POST plus a contacts refetch per open). A token that rides in still claims. */
export const INVITE_CLAIM_DONE_STORAGE_KEY = 'federa_invite_claim_done';

/** localStorage key holding `{ userId, rows }` -- the LAST wishlists list the explore socket delivered on this device,
 *  so the saved icons paint on the next hard load before the socket has connected (`utils/websocket/shared-wishlists.ts`).
 *  Keyed to the viewer inside the value; a guest subject is never written. */
export const WISHLISTS_CACHE_STORAGE_KEY = 'federa_wishlists';

/** The signed guest credential the explore user socket replays as its WS bearer. Paired with `EXPLORE_GUEST_ID_STORAGE_KEY`; a mismatched pair is discarded rather than repaired. */
export const EXPLORE_GUEST_SESSION_TOKEN_STORAGE_KEY = 'federa_explore_guest_session_token';

export const PROPERTIES_CACHE_STORAGE_KEY = 'federa_properties_cache';

/** sessionStorage, NOT localStorage: a new tab must start on the unfiltered feed rather than inheriting a days-old search. */
export const EXPLORE_ACTIVE_SEARCH_STORAGE_KEY = 'federa_explore_active_search';

export const UTM_STORAGE_KEY = 'federa_utm';

export const CALLER_TYPES = [
	'admin',
	'user',
	'system',
	'external',
	'adapter',
	'ai-agent',
	'flow',
	'flow-engine',
	'audit-runner',
	'kaizen-flow',
	'cli',
	'hook',
	'api',
] as const satisfies readonly CallerType[];

export const MFE_BASE_PATH = '/__mfe';

// The auth worker's own mount (`/__auth`), its per-flow path helpers, its wrangler config path and its plain-secret
// list left this leaf with the worker on 2026-09-21. The login flows answer at the single sign-on hub
// (`ifederatedAuthFlowPath`, `const/ifederated-identity.ts`), whose plain secrets are `IFEDERATED_PLAIN_SECRETS` there.

/** Canonical app-name slugs (consumer, admin, and the standalone apps). */
export const EXPLORE_APP_NAME = 'explore-app' as const;

export const ADMIN_APP_NAME = 'mission-control' as const;

export const PROFILE_APP_NAME = 'profile-app' as const;

export const ANALYTICS_APP_NAME = 'analytics-app' as const;

export const MARKET_MAKER_APP_NAME = 'market-maker-app' as const;

export const CONNECT_APP_NAME = 'connect-app' as const;

export const GROUPS_APP_NAME = 'groups-app' as const;

export const NAVIGATOR_APP_NAME = 'navigator-app' as const;

export const ACTIONS_APP_NAME = 'actions-app' as const;

export const NOOR_APP_NAME = 'noor-app' as const;

/** The Kenecting application (`apps/kenecting`): the Connect desk's own host, distinct from `CONNECT_APP_NAME`, which
 *  the desk's standalone `workers.dev` origin registers under. */
export const KENECTING_APP_NAME = 'kenecting-app' as const;

/** The iFederated application (`apps/ifederated`): the single sign-on hub every app sends a visitor to, and the direct
 *  path to that person's profile. */
export const IFEDERATED_APP_NAME = 'ifederated-app' as const;

/** The Federated application (`apps/federated`): Federated Holdings' host for the general ledger. */
export const FEDERATED_APP_NAME = 'federated-app' as const;

/** Fixed role for API token (service-to-service) auth. Never trust client-provided roles.
 *  BOUND to the role SSOT rather than respelling its value: `const/auth.ts` declares itself the "Auth-domain SSOT for
 *  role literals", this file already value-imports `USER_ROLES`, and {@link ROLE_HIERARCHY} three lines below reads
 *  `USER_ROLES.G4` for the same literal -- so the two spellings of one role sat within three lines of each other and a
 *  role rename would have moved the hierarchy key while leaving this header value stale. The sole consumer
 *  (`components/lib/api/fetch-helpers.ts`) sets it as a header string, so the narrowing from `string` to the literal
 *  type is inert there and is what makes a future rename a compile error rather than a silent 403. */
export const API_TOKEN_ROLE = USER_ROLES.G4;

/** Higher number = more permissions. Keys consume `USER_ROLES.*` so the role-literal set is declared ONCE. */
export const ROLE_HIERARCHY = {
	[USER_ROLES.USER]: 0,
	[USER_ROLES.G4]: 50,
	[USER_ROLES.ADMIN]: 100,
	[USER_ROLES.SUPER_ADMIN]: 200,
} as const;

/** Auth-response error-type identifiers (the auth-role-verify / admin-gate mapping contract). */
export const AUTH_ERROR_TYPES = {
	AUTHENTICATION_REQUIRED: 'authentication_required',
	INSUFFICIENT_PRIVILEGES: 'insufficient_privileges',
} as const;

/**
 * How long a socket credential resolver waits for Firebase to resolve auth before treating a null token as "signed out".
 *
 * Bounds the ambiguity in `readWsAuthToken`, which answers null both for a signed-out visitor and for a signed-in one
 * whose bridge has not published yet. Long enough to cover a cold bridge (observed well under a second), short enough
 * that a surface which never marks -- an MFE outside the host shell -- falls through to the guest arm promptly instead
 * of stalling: on timeout the caller takes exactly the pre-wait behaviour.
 */
export const FIREBASE_AUTH_RESOLVE_WAIT_MS = 2000;

// PARTICIPANTS

/** The buy-side `participants.role` a transaction invite defaults to: an additional buyer on the deed, and so a co-owner
 *  once the purchase closes (`OWNER_ROLES`, `const/sql-ownership-list.ts`). A TRANSACTION role, kept OUT of
 *  `PARTICIPANT_ROLES` (the access-role record) per that record's own caution about adding unread members. */
export const TRANSACTION_CO_BUYER_ROLE = 'co-buyer';
